import { ArrowRightLeft, Fingerprint, Mic, PhoneIncoming, ShieldCheck, Users } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

/**
 * Split auth screen: an animated brand showcase on the left (large screens only, decorative) and
 * the form on the right. Used by sign-in, MFA, password reset and invitation pages.
 */
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    // Locked to the viewport: the page never scrolls. Only the form panel may scroll, and only on very small screens.
    <div className="flex h-dvh overflow-hidden">
      <BrandShowcase />
      <main className="relative h-full flex-1 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#38D9FF]/25 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#4263FF]/20 blur-3xl" />
        <div data-form-scroll className="absolute inset-0 flex flex-col overflow-y-auto p-6 sm:p-10">
          <div className="relative m-auto w-full max-w-sm animate-float-in">
            <div className="mb-8 flex justify-center lg:hidden shorter:mb-5">
              <img src="/brand/atlantech/logo.svg" alt="AtlanTech Solution" className="h-16 w-auto shorter:h-12 tiny-h:h-10" />
            </div>
            <div className="mb-6 hidden lg:block">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-600">AtlanTech Solution workspace</p>
            </div>
            <div className="glass rounded-3xl p-7 sm:p-8">
              <h1 className="text-2xl font-semibold tracking-tight text-default">{title}</h1>
              {subtitle && <p className="mt-1.5 text-sm text-muted">{subtitle}</p>}
              <div className="mt-6">{children}</div>
            </div>
            <p className="mt-6 text-center text-xs text-subtle shorter:mt-4 tiny-h:hidden">Every sign-in is recorded in your organization’s audit trail.</p>
          </div>
        </div>
      </main>
    </div>
  );
}

const delay = (s: number): CSSProperties => ({ animationDelay: `${s}s` });

function BrandShowcase() {
  return (
    <aside
      aria-hidden="true"
      className="bg-navy relative hidden h-full w-[52%] max-w-[880px] shrink-0 flex-col overflow-hidden px-12 py-12 text-white lg:flex xl:px-16 short:py-9 shorter:py-7"
    >
      {/* Light field: brand glows and a fading technical grid. */}
      <div className="pointer-events-none absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-[#4263FF]/35 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-48 -right-32 h-[36rem] w-[36rem] rounded-full bg-[#38D9FF]/25 blur-[130px]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at 50% 45%, black 25%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 45%, black 25%, transparent 75%)",
        }}
      />

      <img src="/brand/atlantech/logo-white.svg" alt="" className="relative h-12 w-auto shrink-0 self-start shorter:h-9" />

      {/* Hero visual: takes whatever height is left and scales down in steps on shorter screens. */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center tiny-h:hidden">
        <div className="relative grid h-[360px] w-[520px] max-w-full shrink-0 place-items-center short:scale-[0.8] shorter:scale-[0.62]">
          {[0, 1.2, 2.4].map((d) => (
            <span key={d} className="absolute h-64 w-64 animate-ring-expand rounded-full border border-[#38D9FF]/40" style={delay(d)} />
          ))}
          <span className="absolute h-44 w-44 rounded-full border border-white/10" />
          <span className="absolute h-72 w-72 rounded-full border border-dashed border-white/[0.08]" />
          <div className="relative grid h-32 w-32 place-items-center rounded-[2rem] bg-white/[0.06] shadow-[0_0_80px_-10px_rgb(66_99_255/0.8)] ring-1 ring-white/15 backdrop-blur-md">
            <img src="/brand/atlantech/app-icon.svg" alt="" className="w-24 rounded-[1.6rem]" />
          </div>

          {/* Live call card */}
          <div className="absolute left-0 top-2 w-60 animate-float-y rounded-2xl bg-white/[0.07] p-4 ring-1 ring-white/15 backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#4263FF] to-[#38D9FF] text-sm font-semibold">GH</span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">Grace Hopper</p>
                <p className="flex items-center gap-1.5 text-xs text-white/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgb(52_211_153)]" /> Live · 04:12
                </p>
              </div>
            </div>
            <div className="mt-3 flex h-8 items-center gap-[3px]">
              {Array.from({ length: 22 }, (_, i) => (
                <span
                  key={i}
                  className="h-full w-[3px] origin-center animate-eq rounded-full bg-gradient-to-t from-[#4263FF] to-[#38D9FF]"
                  style={delay(((i * 37) % 11) / 10)}
                />
              ))}
            </div>
          </div>

          {/* Incoming call card */}
          <div className="absolute bottom-6 right-0 w-56 animate-float-y rounded-2xl bg-white/[0.07] p-4 ring-1 ring-white/15 backdrop-blur-xl" style={delay(1.5)}>
            <div className="flex items-center gap-3">
              <span className="relative grid h-9 w-9 place-items-center rounded-full bg-emerald-500/90">
                <span className="absolute inset-0 animate-ring-pulse rounded-full" />
                <PhoneIncoming className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-medium">Incoming call</p>
                <p className="text-xs text-white/60">Support team · 3 agents ringing</p>
              </div>
            </div>
          </div>

          {/* Status pills */}
          <div className="absolute right-6 top-6 flex animate-float-y items-center gap-2 rounded-full bg-white/[0.08] px-3 py-1.5 text-xs ring-1 ring-white/15 backdrop-blur-xl" style={delay(0.8)}>
            <ArrowRightLeft className="h-3.5 w-3.5 text-[#38D9FF]" /> Transfer completed
          </div>
          <div className="absolute bottom-10 left-6 flex animate-float-y items-center gap-2 rounded-full bg-white/[0.08] px-3 py-1.5 text-xs ring-1 ring-white/15 backdrop-blur-xl" style={delay(2.2)}>
            <span className="h-2 w-2 rounded-full bg-rose-400 shadow-[0_0_8px_rgb(251_113_133)]" /> Recording · notice played
          </div>
        </div>
      </div>

      <div className="relative max-w-lg shrink-0 tiny-h:mt-auto">
        <h2 className="text-4xl font-semibold leading-tight tracking-tight xl:text-5xl short:text-4xl shorter:text-3xl">
          Every conversation,{" "}
          <span className="animate-shimmer bg-[length:200%_auto] bg-clip-text text-transparent [background-image:linear-gradient(90deg,#4263FF,#38D9FF,#ffffff,#38D9FF,#4263FF)]">
            crystal clear.
          </span>
        </h2>
        <p className="mt-4 text-base leading-relaxed text-white/65 shorter:mt-3 shorter:text-sm">
          Business calling in your browser. Route, hold, transfer and record calls with enterprise-grade security built in.
        </p>
        <ul className="mt-8 grid grid-cols-2 gap-3 text-sm text-white/80 short:mt-6 shorter:mt-4 shorter:gap-2">
          {[
            [ShieldCheck, "Role-based access control"],
            [Fingerprint, "MFA & complete audit trail"],
            [Users, "Team routing & supervision"],
            [Mic, "Consent-aware recording"],
          ].map(([Icon, label]) => {
            const I = Icon as typeof ShieldCheck;
            return (
              <li key={label as string} className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/[0.07] ring-1 ring-white/10">
                  <I className="h-4 w-4 text-[#38D9FF]" />
                </span>
                {label as string}
              </li>
            );
          })}
        </ul>
        <p className="mt-10 text-xs text-white/40 short:mt-6 shorter:mt-4">© {new Date().getFullYear()} <a href="https://atlantechsolution.com"
          target="_blank"
        >AtlanTech Solution</a></p>
      </div>
    </aside>
  );
}
